import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Siren, Plus, ChevronRight } from 'lucide-react';
import { PageHeader, PageBody } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { toast } from '@/components/ui/Toaster';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { useIncidentList, useDeclareIncident } from '@/api/incidents';
import { relativeTime } from '@/lib/format';
import type { IncidentSeverity } from '@/data/types';

const SEV_LABEL: Record<IncidentSeverity, string> = {
  sev1: 'Critical',
  sev2: 'Major',
  sev3: 'Minor',
};

const SEV_TONE: Record<IncidentSeverity, 'neutral' | 'accent' | 'success' | 'warn' | 'danger'> = {
  sev1: 'danger',
  sev2: 'warn',
  sev3: 'accent',
};

const STATUS_TONE: Record<string, 'neutral' | 'accent' | 'success' | 'warn' | 'danger'> = {
  investigating: 'warn',
  identified: 'accent',
  monitoring: 'accent',
  resolved: 'success',
};

export function IncidentsRoute() {
  const { data: apiList = [] } = useIncidentList();
  const declareMutation = useDeclareIncident();
  const nav = useNavigate();
  const [declareOpen, setDeclareOpen] = useState(false);

  const open = apiList.filter(i => i.status !== 'resolved');
  const resolved = apiList.filter(i => i.status === 'resolved').slice(0, 20);

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Incidents"
        description="Operational issues that needed your attention. Declare one when something's broken; post updates as you investigate."
        actions={
          <Button variant="primary" iconLeft={<Plus size={14} />} onClick={() => setDeclareOpen(true)}>
            Declare incident
          </Button>
        }
      />
      <PageBody>
        <Card className="mb-4">
          <CardHeader
            title={`Active ${open.length > 0 ? `(${open.length})` : ''}`}
            description="Ongoing incidents — click a row for the timeline."
          />
          {open.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="min-w-[720px]">
                <THead>
                  <tr>
                    <TH>Incident</TH>
                    <TH>Severity</TH>
                    <TH>Status</TH>
                    <TH>Affected</TH>
                    <TH>Started</TH>
                    <TH className="w-8"></TH>
                  </tr>
                </THead>
                <TBody>
                  {open.map(i => (
                    <TR key={i.id}>
                      <TD>
                        <Link to={`/incidents/${i.id}`} className="block group">
                          <div className="font-medium text-fg group-hover:text-accent">{i.title}</div>
                          <div className="text-2xs text-fg-subtle font-mono">{i.id}</div>
                        </Link>
                      </TD>
                      <TD><Badge tone={SEV_TONE[i.severity]}>{SEV_LABEL[i.severity]}</Badge></TD>
                      <TD><Badge tone={STATUS_TONE[i.status]} className="capitalize">{i.status}</Badge></TD>
                      <TD className="text-fg-muted text-sm">
                        {i.affected_tenant_ids.length === 0
                          ? '—'
                          : `${i.affected_tenant_ids.length} customer${i.affected_tenant_ids.length === 1 ? '' : 's'}`}
                      </TD>
                      <TD className="text-fg-muted">{relativeTime(i.started_at)}</TD>
                      <TD>
                        <Link to={`/incidents/${i.id}`} className="text-fg-subtle hover:text-accent inline-flex">
                          <ChevronRight size={14} />
                        </Link>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          ) : (
            <div className="px-5 py-10 text-center">
              <Siren size={20} className="mx-auto text-fg-subtle mb-2" />
              <div className="text-sm font-semibold text-fg">All clear</div>
              <div className="text-2xs text-fg-muted mt-0.5">No incidents currently in progress.</div>
            </div>
          )}
        </Card>

        {resolved.length > 0 && (
          <Card>
            <CardHeader title="Recently resolved" description="Last 20 closed incidents." />
            <div className="overflow-x-auto">
              <Table className="min-w-[720px]">
                <THead>
                  <tr>
                    <TH>Incident</TH>
                    <TH>Severity</TH>
                    <TH>Duration</TH>
                    <TH>Resolved</TH>
                    <TH className="w-8"></TH>
                  </tr>
                </THead>
                <TBody>
                  {resolved.map(i => {
                    const durationMin = i.resolved_at
                      ? Math.round((new Date(i.resolved_at).getTime() - new Date(i.started_at).getTime()) / 60000)
                      : 0;
                    return (
                      <TR key={i.id}>
                        <TD>
                          <Link to={`/incidents/${i.id}`} className="block group">
                            <div className="font-medium text-fg group-hover:text-accent">{i.title}</div>
                            <div className="text-2xs text-fg-subtle font-mono">{i.id}</div>
                          </Link>
                        </TD>
                        <TD><Badge tone={SEV_TONE[i.severity]}>{SEV_LABEL[i.severity]}</Badge></TD>
                        <TD className="tabnum text-fg-muted">{formatDuration(durationMin)}</TD>
                        <TD className="text-fg-muted">{i.resolved_at ? relativeTime(i.resolved_at) : '—'}</TD>
                        <TD>
                          <Link to={`/incidents/${i.id}`} className="text-fg-subtle hover:text-accent inline-flex">
                            <ChevronRight size={14} />
                          </Link>
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </div>
          </Card>
        )}

        {apiList.length === 0 && (
          <Card className="px-6 py-12 text-center">
            <Siren size={24} className="mx-auto text-fg-subtle mb-2" />
            <div className="text-md font-semibold text-fg">No incidents recorded</div>
            <p className="text-sm text-fg-muted max-w-md mx-auto mt-1">
              Declare an incident when something goes wrong with a workspace. Updates are appended to a timeline you can refer back to later.
            </p>
          </Card>
        )}
      </PageBody>

      <DeclareIncidentModal
        open={declareOpen}
        onClose={() => setDeclareOpen(false)}
        onSubmit={async (body) => {
          try {
            const incident = await declareMutation.mutateAsync(body);
            toast.warn(
              `${SEV_LABEL[incident.severity]} incident declared`,
              'Audit logged.',
              { to: `/incidents/${incident.id}`, label: 'Open' },
            );
            setDeclareOpen(false);
            nav(`/incidents/${incident.id}`);
          } catch (e: any) {
            toast.danger('Could not declare incident', e?.message ?? 'Unknown error');
          }
        }}
      />
    </>
  );
}

function formatDuration(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function DeclareIncidentModal({
  open,
  onClose,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: {
    title: string;
    severity: IncidentSeverity;
    public_summary?: string;
    first_update?: string;
  }) => void;
}) {
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>('sev2');
  const [firstUpdate, setFirstUpdate] = useState('');

  function reset() {
    setTitle('');
    setSeverity('sev2');
    setFirstUpdate('');
  }

  function submit() {
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      severity,
      first_update: firstUpdate.trim() || undefined,
    });
    reset();
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      size="md"
      title="Declare incident"
      description="Opens a tracked incident with a timeline you can append updates to. Audit-logged."
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          <Button variant="danger" onClick={submit} disabled={!title.trim()}>
            Declare
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Title <span className="text-[color:var(--danger)]">*</span>
          </label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. nousviz.online returning 502s"
            autoFocus
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
          />
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">Severity</label>
          <select
            value={severity}
            onChange={e => setSeverity(e.target.value as IncidentSeverity)}
            className="w-full h-8 px-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
          >
            <option value="sev1">Critical — workspace down or data at risk</option>
            <option value="sev2">Major — significant degradation</option>
            <option value="sev3">Minor — internal-only, no customer impact</option>
          </select>
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            First note <span className="text-fg-subtle font-normal lowercase tracking-normal">(optional)</span>
          </label>
          <textarea
            value={firstUpdate}
            onChange={e => setFirstUpdate(e.target.value)}
            placeholder="What you know so far — symptoms, scope, where you're looking."
            rows={3}
            className="w-full px-3 py-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm leading-snug resize-none"
          />
        </div>
      </div>
    </Modal>
  );
}
